import { FC, useState, useEffect } from "react";
import {
  styled,
  Container,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Link
} from "@mui/material";
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { useTranslation } from 'react-i18next';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { useAuth0Token, getLink } from "../common/http-requests";
import { linkRequestType, linkResponseType } from "../common/type";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import { themeColors } from "../common/colors";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { pageMt } from "../common/util";
import { useLanguage } from "../hooks/useStoreGetters";

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const LinkPage: FC = () => {
  const { t } = useTranslation();
  const { isDesktop } = useCustomMediaQuery();
  const language = useLanguage();
  const [isSending, setIsSending] = useState(true);
  const [links, setLinks] = useState<linkResponseType[]>([]);
  const { getToken } = useAuth0Token();

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });
  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getLinkData();
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getLinkData = async () => {
    setIsSending(true);
    try {
      const requestParams: linkRequestType = {
        language,
      };
      const data = await getLink(
        await getToken(),
        requestParams
      );
      setLinks(data.links);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
              <Title>
                <Typography
                  variant="h5"
                  style={{ marginTop: 10, textAlign: "left" }}
                >
                  <LinkOutlinedIcon sx={{ margin: '.5rem .8rem' }} />{t("menu.link")}
                </Typography>
              </Title>
              <List>
                {links.map((link: linkResponseType) => (
                  <ListItem key={link.link_name} disablePadding>
                    <ListItemButton component={Link} href={link.link_url} target="_blank" rel="noopener noreferrer">
                      <ListItemText
                        primary={link.link_name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: `${themeColors[0]}`,
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default LinkPage;
